import { normalizeWidgetTheme, APIURL, appendFlatpickrBaseStyles, initFlatpickrInShadow, cancelSelectEvent, getCSSStyles, getWidgetDataURI, apiGet, toggleWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML } from './../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';
import flatpickr from 'flatpickr';
import { format } from 'date-fns';

export class TraderverseIPOTimelineCalendar {
  private container: HTMLElement;
  private config: WidgetConfig;
  private filters: {
    startDate?: string | null;
    endDate?: string | null;
    exchanges: string[];
    statuses: string[];
    groupBy: 'day' | 'week' | 'month' | 'quarter';
    onlyWithPriceRange: boolean;
    minMarketCap?: number | null;
  };
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private widgetResizeObserver: ResizeObserver | null = null;
  private datePickerInstance: any = null;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.filters = {
      startDate: '2025-01-01',
      endDate: '2025-12-31',
      exchanges: ['NASDAQ', 'NYSE'],
      statuses: ['Expected', 'Priced'],
      groupBy: 'quarter',
      onlyWithPriceRange: true,
      minMarketCap: 500000000
    };
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }


  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;

    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    this.attachStyles();
    await this.setupChart();
    if (widget) widget.classList.remove('loading');
    this.emit('ready', {});
    this.config.onReady?.();
    cancelSelectEvent(this.shadowRoot);
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title market">${this.getTitleIconSvg()} IPO Timeline Calendar</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<div class="item">
                <button class="filters-toggle" type="button"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
              </div></div>
        </div>
        <div class="inBody">

          <div class="optionsRow">
            <div class="item dateRange">
              <input type="text" class="date-input" />
              <i><svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M9.91667 1.16667H8.75V0.583333C8.75 0.428624 8.68854 0.280251 8.57915 0.170854C8.46975 0.0614581 8.32138 0 8.16667 0C8.01196 0 7.86358 0.0614581 7.75419 0.170854C7.64479 0.280251 7.58333 0.428624 7.58333 0.583333V1.16667H4.08333V0.583333C4.08333 0.428624 4.02188 0.280251 3.91248 0.170854C3.80308 0.0614581 3.65471 0 3.5 0C3.34529 0 3.19692 0.0614581 3.08752 0.170854C2.97813 0.280251 2.91667 0.428624 2.91667 0.583333V1.16667H1.75C1.28587 1.16667 0.840752 1.35104 0.512563 1.67923C0.184375 2.00742 0 2.45254 0 2.91667V9.91667C0 10.3808 0.184375 10.8259 0.512563 11.1541C0.840752 11.4823 1.28587 11.6667 1.75 11.6667H9.91667C10.3808 11.6667 10.8259 11.4823 11.1541 11.1541C11.4823 10.8259 11.6667 10.3808 11.6667 9.91667V2.91667C11.6667 2.45254 11.4823 2.00742 11.1541 1.67923C10.8259 1.35104 10.3808 1.16667 9.91667 1.16667ZM10.5 9.91667C10.5 10.0714 10.4385 10.2198 10.3291 10.3291C10.2198 10.4385 10.0714 10.5 9.91667 10.5H1.75C1.59529 10.5 1.44692 10.4385 1.33752 10.3291C1.22812 10.2198 1.16667 10.0714 1.16667 9.91667V5.83333H10.5V9.91667ZM10.5 4.66667H1.16667V2.91667C1.16667 2.76196 1.22812 2.61358 1.33752 2.50419C1.44692 2.39479 1.59529 2.33333 1.75 2.33333H2.91667V2.91667C2.91667 3.07138 2.97813 3.21975 3.08752 3.32915C3.19692 3.43854 3.34529 3.5 3.5 3.5C3.65471 3.5 3.80308 3.43854 3.91248 3.32915C4.02188 3.21975 4.08333 3.07138 4.08333 2.91667V2.33333H7.58333V2.91667C7.58333 3.07138 7.64479 3.21975 7.75419 3.32915C7.86358 3.43854 8.01196 3.5 8.16667 3.5C8.32138 3.5 8.46975 3.43854 8.57915 3.32915C8.68854 3.21975 8.75 3.07138 8.75 2.91667V2.33333H9.91667C10.0714 2.33333 10.2198 2.39479 10.3291 2.50419C10.4385 2.61358 10.5 2.76196 10.5 2.91667V4.66667Z" fill="#9F9FA2"/> </svg> </i>
            </div>

            <span></span>
            
            <div class="filtersBox sideBar hidden">

              <div class="item checkBoxes checkBoxes02" id="filterExchange">
                <label class="filterGroupLabel">Exchange</label>
                <div class="checkBoxField">
                  <input id="filterExchangeNasdaq" type="checkbox" value="NASDAQ" />
                  <label for="filterExchangeNasdaq">NASDAQ <i>✓</i></label>
                </div>
                <div class="checkBoxField">
                  <input id="filterExchangeNyse" type="checkbox" value="NYSE" />
                  <label for="filterExchangeNyse">NYSE <i>✓</i></label>
                </div>
                <div class="checkBoxField">
                  <input id="filterExchangeAmex" type="checkbox" value="AMEX" />
                  <label for="filterExchangeAmex">AMEX <i>✓</i></label>
                </div>
              </div>
              <div class="item checkBoxes checkBoxes02" id="filterStatus">
                <label class="filterGroupLabel">Status</label>
                <div class="checkBoxField">
                  <input id="filterStatusExpected" type="checkbox" value="Expected" />
                  <label for="filterStatusExpected">Expected <i>✓</i></label>
                </div>
                <div class="checkBoxField">
                  <input id="filterStatusPriced" type="checkbox" value="Priced" />
                  <label for="filterStatusPriced">Priced <i>✓</i></label>
                </div>
                <div class="checkBoxField">
                  <input id="filterStatusCompleted" type="checkbox" value="Completed" />
                  <label for="filterStatusCompleted">Completed <i>✓</i></label>
                </div>
                <div class="checkBoxField">
                  <input id="filterStatusWithdrawn" type="checkbox" value="Withdrawn" />
                  <label for="filterStatusWithdrawn">Withdrawn <i>✓</i></label>
                </div>
                <div class="checkBoxField">
                  <input id="filterStatusPostponed" type="checkbox" value="Postponed" />
                  <label for="filterStatusPostponed">Postponed <i>✓</i></label>
                </div>
              </div>
              <div class="item">
                <label>Group By</label>
                <select id="filterGroupBy">
                  <option value="week">Week</option>
                  <option value="month" selected>Month</option>
                  <option value="quarter">Quarter</option>
                </select>
              </div>
              <div class="item">
                <div class="checkBoxField">
                    <input id="filterOnlyWithPrice" type="checkbox" />
                    <label for="filterOnlyWithPrice">With Price Range <i>✓</i></label>
                </div>
              </div>
              <div class="item">
                <label>Min Market Cap</label>
                <input id="filterMinMarketCap" type="number" placeholder="0" />
              </div>
            </div>

          </div>

          <div class="ipoTimeline" id="eventsContainer"></div>
        </div>
      </div>
    `;

    this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);
    this.initDatePicker();
    // const style = document.createElement('style');
    // style.textContent = ``;
    // this.shadowRoot.appendChild(style);

    setTimeout(() => {
      const dateInput = this.shadowRoot.querySelector('.date-input') as HTMLInputElement | null;
      const exchEl = this.shadowRoot.querySelector('#filterExchange') as HTMLElement | null;
      const statusEl = this.shadowRoot.querySelector('#filterStatus') as HTMLElement | null;
      const groupEl = this.shadowRoot.querySelector('#filterGroupBy') as HTMLSelectElement | null;
      const onlyPriceEl = this.shadowRoot.querySelector('#filterOnlyWithPrice') as HTMLInputElement | null;
      const minCapEl = this.shadowRoot.querySelector('#filterMinMarketCap') as HTMLInputElement | null;

      if (dateInput) {
        try {
          if (this.datePickerInstance && this.datePickerInstance.setDate) {
            const defaults: string[] = [];
            if (this.filters.startDate) defaults.push(this.filters.startDate);
            if (this.filters.endDate) defaults.push(this.filters.endDate as string);
            if (defaults.length) this.datePickerInstance.setDate(defaults);
          }
        } catch (err) {
          // ignore
        }
      }
      if (exchEl) {
        const checkboxes = Array.from(exchEl.querySelectorAll('input[type="checkbox"]')) as HTMLInputElement[];
        checkboxes.forEach(input => {
          input.addEventListener('change', () => {
            this.filters.exchanges = checkboxes.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.value);
            this.setupChart();
          });
        });
      }

      if (statusEl) {
        const checkboxes = Array.from(statusEl.querySelectorAll('input[type="checkbox"]')) as HTMLInputElement[];
        checkboxes.forEach(input => {
          input.addEventListener('change', () => {
            this.filters.statuses = checkboxes.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.value);
            this.setupChart();
          });
        });
      }
      if (groupEl) groupEl.addEventListener('change', () => { this.filters.groupBy = (groupEl.value as any) || 'quarter'; this.setupChart(); });
      if (onlyPriceEl) onlyPriceEl.addEventListener('change', () => { this.filters.onlyWithPriceRange = !!onlyPriceEl.checked; this.setupChart(); });
      if (minCapEl) minCapEl.addEventListener('change', () => { const v = parseFloat(minCapEl.value || '0'); this.filters.minMarketCap = v > 0 ? v : null; this.setupChart(); });

      if (this.filters.startDate && this.filters.endDate) {
        const d = this.shadowRoot.querySelector('.date-input') as HTMLInputElement | null;
        if (d) d.value = `${this.filters.startDate} to ${this.filters.endDate}`;
      }
      if (exchEl && this.filters.exchanges) {
        const checkboxes = Array.from(exchEl.querySelectorAll('input[type="checkbox"]')) as HTMLInputElement[];
        checkboxes.forEach((checkbox) => {
          checkbox.checked = this.filters.exchanges.includes(checkbox.value);
        });
      }
      if (statusEl && this.filters.statuses) {
        const checkboxes = Array.from(statusEl.querySelectorAll('input[type="checkbox"]')) as HTMLInputElement[];
        checkboxes.forEach((checkbox) => {
          checkbox.checked = this.filters.statuses.includes(checkbox.value);
        });
      }
      if (groupEl) groupEl.value = this.filters.groupBy;
      if (onlyPriceEl) onlyPriceEl.checked = !!this.filters.onlyWithPriceRange;
      if (minCapEl && this.filters.minMarketCap) minCapEl.value = String(this.filters.minMarketCap);
      const toggleBtn = this.shadowRoot.querySelector('.filters-toggle') as HTMLElement | null;
      const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
      if (toggleBtn && filtersBox) {
        toggleBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          filtersBox.classList.toggle('hidden');
        });
      }
    }, 0);
  }

  private async attachStyles(): Promise<void> {
    try {
      appendFlatpickrBaseStyles(this.shadowRoot);
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private initDatePicker(): void {
    try {
      const input = this.shadowRoot.querySelector('.date-input') as HTMLInputElement | null;
      if (!input) return;

      if (this.datePickerInstance && this.datePickerInstance.destroy) {
        this.datePickerInstance.destroy();
        this.datePickerInstance = null;
      }

      const defaultDates: string[] = [];
      if (this.filters.startDate) defaultDates.push(this.filters.startDate);
      if (this.filters.endDate) defaultDates.push(this.filters.endDate as string);

      this.datePickerInstance = initFlatpickrInShadow(input, this.shadowRoot, {
        mode: 'range',
        dateFormat: 'Y-m-d',
        defaultDate: defaultDates.length ? defaultDates : undefined,
        onClose: (selectedDates: Date[]) => {
          if (selectedDates.length === 2) {
            const [start, end] = selectedDates;
            const s = format(start, 'yyyy-MM-dd');
            const e = format(end, 'yyyy-MM-dd');
            this.filters.startDate = s;
            this.filters.endDate = e;
            input.value = `${s} to ${e}`;
            this.setupChart();
          } else if (selectedDates.length === 1) {
            const s = format(selectedDates[0], 'yyyy-MM-dd');
            this.filters.startDate = s;
            this.filters.endDate = s;
            input.value = s;
            this.setupChart();
          }
        }
      });
    } catch (err) {
      console.warn('Date picker init failed:', err);
    }
  }

  private transformAPIResponse(raw: any) {
  if (!raw || !raw.timeline) return { months: [] };

  const months = raw.timeline.map((p: any) => {
    return {
      title: p.period_label,
      items: p.ipos.map((ipo: any) => ({
        name: ipo.company_name,
        date: ipo.ipo_date,
        exchange: ipo.exchange,
        status: ipo.status,
        price: ipo.price_range,
        symbol: ipo.symbol
      }))
    };
  });

  return { months };
}


  private async setupChart(): Promise<void> {
  try {
    const url = this.buildFetchURL();
    const response = await apiGet({
      method: 'GET',
      url,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) throw new Error("Failed to fetch data");

    const rawData = await response.json();
    this.currentData = this.transformAPIResponse(rawData.data);

    this.updateDisplay();
    this.emit('dataUpdate', this.currentData);

  } catch (err) {
    console.error("Error fetching chart data:", err);
  }
}

  private buildFetchURL(): string {
    const base = `${APIURL}/v1/discover/ipo-timeline-calendar`;
    const params: string[] = [];

    if (this.filters.startDate) params.push(`p_start_date=${encodeURIComponent(this.filters.startDate)}`);
    if (this.filters.endDate) params.push(`p_end_date=${encodeURIComponent(this.filters.endDate)}`);

    if (this.filters.exchanges && this.filters.exchanges.length) {
      for (const e of this.filters.exchanges) params.push(`p_exchanges=${encodeURIComponent(e)}`);
    }

    if (this.filters.statuses && this.filters.statuses.length) {
      for (const s of this.filters.statuses) params.push(`p_statuses=${encodeURIComponent(s)}`);
    }

    if (this.filters.groupBy) params.push(`p_group_by=${encodeURIComponent(this.filters.groupBy)}`);
    if (this.filters.onlyWithPriceRange) params.push(`p_only_with_price_range=true`);

    if (this.filters.minMarketCap && typeof this.filters.minMarketCap === 'number') params.push(`p_min_market_cap=${this.filters.minMarketCap}`);

    if (!this.filters.startDate && !this.filters.endDate) {
      const now = new Date();
      const start = `${now.getFullYear()}-01-01`;
      const end = `${now.getFullYear()}-12-31`;
      params.push(`p_start_date=${start}`);
      params.push(`p_end_date=${end}`);
    }

    const query = params.join('&');
    return `${base}${query ? '?' + query : ''}`;
  }


  private renderIPOTimeline(data: any): void {
    const container = this.shadowRoot.querySelector('#eventsContainer') as HTMLElement;
if (!container) return;

// clear old attribute first
container.removeAttribute('data-testing-id');
    if (!container) return;

    if (!data || !data.months || data.months.length === 0) {
      container.innerHTML = getNoDataHTML();
      return;
    }
    const hasData = data?.months?.length > 0;

if (hasData) {
  container.setAttribute('data-testing-id', 'ipo-timeline-has-data');
} else {
  container.removeAttribute('data-testing-id');
}

    let html = '';
    for (const month of data.months) {
      html += `<headerRow><h4>${month.title} (${month.items.length} IPOs)</h4></headerRow>
        <div class="inner">`;

      for (const item of month.items) {
        const badgeClass = item.status?.toLowerCase() === 'priced' ? 'priced' : item.status?.toLowerCase() === 'expected' ? 'expected' : item.status?.toLowerCase() === 'postponed' ? 'postponed' : 'planned';
        html += `
        
                <div class="item">
                    <div class="mainRow rowitm">
                        <div class="colItm">
                            <h4>${item.name}</h4>
                            <p>${item.symbol}</p>
                        </div>
                        <div class="colItm"><span class="badge btnBagde ${badgeClass}">${item.status}</span></div>
                    </div>
                    <div class="subRow rowitm">
                        <div class="colItm">
                            <p>Date <strong>${item.date}</strong></p>
                        </div>
                        <div class="colItm text-center">
                            <p>Exchange <strong>${item.exchange}</strong></p>
                        </div>
                        <div class="colItm text-right">
                            <p>Price <strong class="text-green">${item.price ?? '-'}</strong></p>
                        </div>
                    </div>
                </div>
                `;
      }


      html += `</div>`;
    }

    container.innerHTML = `

    <div class="ipoTimelineMain">
    ${html}
    </div></div>
    `;
  }

  private updateDisplay(): void {
    if (!this.currentData) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.renderIPOTimeline(this.currentData);
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
  }

  public setSymbol(symbol: string): void {
    this.config.symbol = symbol;
    this.setupChart();
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }

  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
  }

}

declare global { interface Window { TraderverseIPOTimelineCalendar: typeof TraderverseIPOTimelineCalendar; } }
if (typeof window !== 'undefined') { window.TraderverseIPOTimelineCalendar = TraderverseIPOTimelineCalendar; }
